/**
 * Which tenant logo the play portal shows on each theme, and how it is sized.
 *
 * Split out of PlayerBrandLogo because the interesting part is the FALLBACK, and
 * a fallback that lives inside a component with a useEffect and a fetch is a
 * fallback nobody can test. QA 2026-09-17 raised both halves:
 *
 *   "the HumanFirewall logo is very small"  → the sizing constant below
 *   "upload both a dark and a light logo"   → the two slots resolved here
 */

export interface PlayerLogoInput {
  /** branding.logoPath — the light-surface logo, and the historical single slot. */
  logoPath?: string | null
  /** branding.logoPathDark — optional dark-surface variant. */
  logoPathDark?: string | null
}

export interface PlayerLogos {
  /** Path to show on the light theme, or null to fall back to the HF wordmark. */
  light: string | null
  /** Path to show on the dark theme. */
  dark: string | null
}

/**
 * Tailwind classes for a logo in the play chrome.
 *
 * `h-10` (40px), not the old `h-7` (28px): 28px in a nav bar is what "very
 * small" meant (QA 2026-09-17), and it was applied to the tenant logo as well
 * as the HumanFirewall wordmark, so a square tenant logo rendered 28x28.
 *
 * A DEFINITE height, not `max-h-10`. The first version of this constant used
 * max-height so a wide mark could keep its own ratio, and the browser pass on
 * the running stack measured the result: the next/image wordmark rendered at
 * 0x0. The <img> sits in a flex <Link> whose width comes from the image, and
 * Tailwind's preflight gives every img `max-width:100%` — a percentage against
 * a parent that is waiting on the image, which Chrome resolves to 0 when no
 * height pins the box. Live measurement in that header: `h-7` 176x28,
 * `h-10` 251x40, `max-h-10` 0x0. So the height is fixed and the width follows
 * the ratio (`w-auto`); `max-w-[280px]` + `object-contain` keep a wide banner
 * from pushing the nav off screen without distorting it.
 *
 * WHY 48px AND NOT 40. Sizing purely by height is unfair to a COMPACT lockup.
 * The logo one tenant uploaded is 512x256 — a shield plus wordmark in a 2:1
 * box — so at 40px tall it got only 80px of width and the text inside it was
 * roughly 12px: legible in principle, unreadable in practice ("the logo in the
 * playground is still not clear", QA 2026-09-18). The HumanFirewall default is
 * 6.29:1 and took 251px at the same height, which is why only the compact logo
 * looked wrong. 48px gives that 2:1 lockup 96px and keeps the play header at a
 * sane ~72px; the wide wordmark is then bounded by max-w instead, and
 * object-contain letterboxes it rather than distorting.
 */
export const PLAYER_LOGO_MAX_HEIGHT_CLASS =
  'h-12 w-auto max-w-[280px] object-contain'

/**
 * Cross-tenant mock asset that must never reach a real tenant's header.
 *
 * Carried over verbatim from PlayerBrandLogo's own defence-in-depth check. It
 * guards against a mock fixture leaking through the play-scoped settings
 * response — a branding incident, not a cosmetic bug.
 */
function isMockAsset(path: string): boolean {
  return path.toLowerCase().includes('wipro')
}

/** Trim, and treat blank or mock values as absent. */
function clean(value: string | null | undefined): string | null {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v || isMockAsset(v)) return null
  return v
}

/**
 * Resolve the two theme slots.
 *
 * EITHER slot alone fills BOTH. That is the back-compat rule and the reason
 * this function exists: every tenant that has a logo today has only
 * `logoPath`, and their portal must look exactly the same afterwards. The
 * symmetric case (a lone dark upload) is handled the same way, because an admin
 * who filled one slot has expressed a preference and rendering nothing would
 * read as a failed upload.
 *
 * Returns nulls when neither slot holds anything, which is the caller's signal
 * to render the HumanFirewall wordmark instead.
 */
export function resolvePlayerLogos(input: PlayerLogoInput): PlayerLogos {
  const light = clean(input.logoPath)
  const dark = clean(input.logoPathDark)

  if (!light && !dark) return { light: null, dark: null }

  return {
    light: light ?? dark,
    dark: dark ?? light,
  }
}
